import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import axios from "axios";
import Decimal from "decimal.js";

import Images from "../../../utils/Images";
import { API } from "../../../utils/Api";
import { CategoryFilterChip } from "../../../components/FilterChip";
import SegmentedControls from "../../../components/Reusables/SegmentedControls";
import PaymentProcessing from "../../../components/Orders/PaymentProcessing";

const LOCAL_CART_KEY = "cafeLocalCart";

const toDecimal = (value) => {
  try {
    return new Decimal(value ?? 0);
  } catch (error) {
    return new Decimal(0);
  }
};

const toMoneyNumber = (value) =>
  Number(toDecimal(value).toDecimalPlaces(2, Decimal.ROUND_DOWN).toString());

const calculateTaxAmount = (rateInput, quantityInput, taxPercentInput) => {
  const rate = toDecimal(rateInput);
  const quantity = toDecimal(quantityInput);
  const taxPercent = toDecimal(taxPercentInput);

  const taxable = rate.mul(quantity).toDecimalPlaces(2, Decimal.ROUND_DOWN);

  const taxAmount = taxable
    .mul(taxPercent)
    .div(100)
    .toDecimalPlaces(2, Decimal.ROUND_DOWN);

  const grandTotal = taxable
    .add(taxAmount)
    .toDecimalPlaces(2, Decimal.ROUND_DOWN);

  return {
    taxable: Number(taxable.toString()),
    taxAmount: Number(taxAmount.toString()),
    grandTotal: Number(grandTotal.toString()),
  };
};

function CafeOrdering() {
  const token = localStorage.getItem("token");
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const [outletData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("outletData")) || null;
    } catch (error) {
      console.error("Invalid outletData:", error);
      return null;
    }
  });

  const initialOrderType =
    localStorage.getItem("selectedOrderType") ||
    localStorage.getItem("orderType") ||
    "PICKUP";

  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [addonDrawerOpen, setAddonDrawerOpen] = useState(false);
  const [editingCartItemId, setEditingCartItemId] = useState(null);
  const addonGroupRefs = useRef({});
  const [missingRequiredAddonId, setMissingRequiredAddonId] = useState(null);
  const [exhaustedAddonId, setExhaustedAddonId] = useState(null);
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [loading, setLoading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState(
    initialOrderType === "TAKEAWAY" ? "TAKEAWAY" : "DINE_IN",
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("UPI");
  const [checkingStock, setCheckingStock] = useState(false);
  const [cartProductRawMaterials, setCartProductRawMaterials] = useState([]);

  const [cart, setCart] = useState(() => {
    try {
      const storedCart = JSON.parse(localStorage.getItem(LOCAL_CART_KEY));

      return Array.isArray(storedCart) ? storedCart : [];
    } catch (error) {
      console.error("Invalid local cart:", error);
      return [];
    }
  });

  const [message, setMessage] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  const getId = (item) => {
    if (!item) {
      return null;
    }

    if (typeof item === "string") {
      return item;
    }

    return item?._id || item?.value || null;
  };

  // Addon `value` can be a populated object (use _id) or a display string (not an id).
  const getValueMasterId = (value) => {
    if (!value || typeof value !== "object") {
      return null;
    }

    return value._id || null;
  };

  const isOutletExcluded = (excludedOutlets = []) => {
    if (!Array.isArray(excludedOutlets) || !outletData?._id) {
      return false;
    }

    return excludedOutlets.some((outlet) => getId(outlet) === outletData._id);
  };

  const normalizeExcludedValues = (excludedValues = []) => {
    if (!Array.isArray(excludedValues)) {
      return [];
    }

    return excludedValues.map((value) => getId(value)).filter(Boolean);
  };

  const getProductPrice = (product) => {
    return toMoneyNumber(
      product?.newOfferPrice || product?.offerPrice || product?.price || 0,
    );
  };

  const getProductMrp = (product) => {
    return toMoneyNumber(product?.newMrp ?? product?.mrp ?? 0);
  };

  // Backend validates mrpRate against product MRP only (addons are separate).
  const calculateMrpRate = (productMrp) =>
    toDecimal(productMrp).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();

  const getProductTaxPercent = (product) => {
    return Number(
      product?.productTaxPercent ??
        product?.taxPercent ??
        product?.taxPercentage ??
        product?.gstPercent ??
        product?.gstPercentage ??
        0,
    );
  };

  const getAddonValueLabel = (addonValue) => {
    if (typeof addonValue?.value === "object") {
      return (
        addonValue?.value?.value ||
        addonValue?.value?.label ||
        addonValue?.value?.name ||
        addonValue?.subLabel ||
        "Addon"
      );
    }

    return addonValue?.value || addonValue?.subLabel || "Addon";
  };

  const flattenCategoriesToProducts = (categoryList = []) => {
    if (!Array.isArray(categoryList)) {
      return [];
    }

    return categoryList.flatMap((category) => {
      const categoryProducts = Array.isArray(category?.products)
        ? category.products
        : [];

      return categoryProducts.map((product) => ({
        ...product,
        category: {
          _id: category?._id,
          name: category?.name,
        },
      }));
    });
  };

  const saveCart = (updatedCart) => {
    setCart(updatedCart);

    localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(updatedCart));
  };

  const showMessage = (text, severity = "success") => {
    setMessage({
      open: true,
      text,
      severity,
    });
  };

  const filterProducts = ({
    search = searchQuery,
    categoryId = selectedCategoryId,
    sourceProducts = allProducts,
  } = {}) => {
    const normalizedSearch = String(search || "")
      .trim()
      .toLowerCase();

    let filteredProducts = Array.isArray(sourceProducts)
      ? [...sourceProducts]
      : [];

    if (categoryId) {
      filteredProducts = filteredProducts.filter(
        (product) => product?.category?._id === categoryId,
      );
    }

    if (normalizedSearch) {
      filteredProducts = filteredProducts.filter((product) => {
        const productName = String(product?.name || "").toLowerCase();

        return productName.includes(normalizedSearch);
      });
    }

    setProducts(filteredProducts);
  };

  const getCategoriesProductsList = async () => {
    if (!outletData?._id) {
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(
        `${API.BASE_URL}product/getProductsWithAddons`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
          params: {
            outletId: outletData._id,
            orderType: "PICKUP",
          },
        },
      );

      let fetchedCategories = response?.data?.data?.categories || [];

      if (!Array.isArray(fetchedCategories)) {
        fetchedCategories = [];
      }

      fetchedCategories = fetchedCategories
        .filter((category) => !category?.isEventCategory)
        .map((category) => ({
          ...category,
          products: Array.isArray(category?.products)
            ? category.products.filter((product) => !product?.isEventProduct)
            : [],
        }));

      const flattenedProducts = flattenCategoriesToProducts(fetchedCategories);

      setCategories(fetchedCategories);
      setAllProducts(flattenedProducts);
      setProducts(flattenedProducts);
    } catch (error) {
      console.error("Error fetching products:", error?.response?.data || error);

      showMessage(
        error?.response?.data?.message || "Unable to fetch products.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const isAddonValueExhausted = (valueItem) => {
    if (!valueItem) {
      return false;
    }

    if (valueItem?.isExhausted) {
      return true;
    }

    return isOutletExcluded(valueItem?.excludedOutlets || []);
  };

  const enrichAddonValue = (valueItem, addon) => ({
    ...valueItem,
    label: getAddonValueLabel(valueItem),
    price: Number(valueItem?.price || 0),
    isDefault: Boolean(valueItem?.isDefault),
    isExhausted: isAddonValueExhausted(valueItem),
    addonAllocationId: addon?._id,
    addonId: getId(addon?.addon) || addon?._id,
    addonTitle: addon?.title,
    addonExcludedOutlets: addon?.excludedOutlets || [],
  });

  const isPickupCompatibleOrderType = (orderType) => {
    if (!orderType) {
      return true;
    }

    const normalized = String(orderType).toUpperCase();

    return normalized === "PICKUP" || normalized === "ALL";
  };

  const buildVisibleAddons = (product) => {
    const productAddons = Array.isArray(product?.addons) ? product.addons : [];

    return productAddons
      .filter((allocation) => {
        if (allocation?.status === "INACTIVE") {
          return false;
        }

        if (isOutletExcluded(allocation?.excludedOutlets || [])) {
          return false;
        }

        const addonMaster =
          typeof allocation?.addon === "object" ? allocation.addon : {};

        return isPickupCompatibleOrderType(
          allocation?.orderType ?? addonMaster?.orderType,
        );
      })
      .map((allocation) => {
        const addonMaster =
          typeof allocation?.addon === "object" ? allocation.addon : {};

        const excludedValueIds = normalizeExcludedValues(
          allocation?.excludedValues || [],
        );

        const allocationValues = Array.isArray(allocation?.values)
          ? allocation.values
          : [];

        const title = addonMaster?.title || allocation?.title || "Choose addon";

        const enrichedAllocation = {
          ...allocation,
          title,
          badgeImage: addonMaster?.badgeImage || allocation?.badgeImage || null,
          isOptional: addonMaster?.isOptional ?? allocation?.isOptional ?? true,
          isMultipleSelect:
            addonMaster?.isMultipleSelect ??
            allocation?.isMultipleSelect ??
            false,
        };

        const values = allocationValues
          .filter((valueItem) => {
            if (!valueItem) {
              return false;
            }

            const valueMaster =
              typeof valueItem?.value === "object" ? valueItem.value : {};

            if (
              !isPickupCompatibleOrderType(
                valueItem?.orderType ?? valueMaster?.orderType,
              )
            ) {
              return false;
            }

            const valueId = getId(valueItem?.value);
            const isDefault = Boolean(valueItem?.isDefault);

            const isRootExcluded =
              valueId && excludedValueIds.includes(valueId);

            // Fully removed from this product allocation
            if (isRootExcluded) {
              return false;
            }

            const allocationValueStatus = valueItem?.status || "ACTIVE";

            const masterValueStatus = valueMaster?.status || "ACTIVE";

            const isInactive =
              allocationValueStatus === "INACTIVE" ||
              masterValueStatus === "INACTIVE";

            const isExhausted = isAddonValueExhausted(valueItem);

            // Keep defaults even when exhausted/inactive so
            // they can be preselected and the user warned.
            if (isDefault) {
              return true;
            }

            return !isInactive && !isExhausted;
          })
          .map((valueItem) => enrichAddonValue(valueItem, enrichedAllocation));

        return {
          ...enrichedAllocation,
          values,
        };
      })
      .filter((addon) => addon.values.length > 0);
  };

  const buildDefaultAddonSelections = (addons = []) => {
    return addons.reduce((selections, addon) => {
      const defaultValues = (addon?.values || []).filter(
        (value) => value?.isDefault,
      );

      if (!defaultValues.length) {
        return selections;
      }

      const selectedValues = addon?.isMultipleSelect
        ? defaultValues
        : [defaultValues[0]];

      return {
        ...selections,
        [addon._id]: selectedValues,
      };
    }, {});
  };

  const findExhaustedSelectedAddon = (addons = [], selections = {}) => {
    return addons.find((addon) => {
      const selectedValues = selections?.[addon?._id] || [];

      return selectedValues.some((value) => value?.isExhausted);
    });
  };

  const visibleAddons = useMemo(
    () => buildVisibleAddons(selectedProduct),
    [selectedProduct, outletData?._id],
  );

  const selectedAddonValues = useMemo(() => {
    return Object.values(selectedAddons).flatMap((addonValues) =>
      Array.isArray(addonValues) ? addonValues : [],
    );
  }, [selectedAddons]);

  const addonsTotal = useMemo(() => {
    return selectedAddonValues
      .reduce(
        (total, addonValue) => total.plus(toDecimal(addonValue?.price || 0)),
        new Decimal(0),
      )
      .toDecimalPlaces(2, Decimal.ROUND_DOWN)
      .toNumber();
  }, [selectedAddonValues]);

  const currentItemUnitPrice = useMemo(() => {
    return toDecimal(getProductPrice(selectedProduct))
      .plus(toDecimal(addonsTotal))
      .toDecimalPlaces(2, Decimal.ROUND_DOWN)
      .toNumber();
  }, [selectedProduct, addonsTotal]);

  const currentItemTax = useMemo(() => {
    return calculateTaxAmount(
      currentItemUnitPrice,
      quantity,
      getProductTaxPercent(selectedProduct),
    );
  }, [currentItemUnitPrice, quantity, selectedProduct]);

  const currentItemTotal = currentItemTax.grandTotal;

  const cartSummary = useMemo(() => {
    return cart.reduce(
      (summary, cartItem) => ({
        taxable: toDecimal(summary.taxable)
          .plus(toDecimal(cartItem?.taxable || 0))
          .toDecimalPlaces(2, Decimal.ROUND_DOWN)
          .toNumber(),
        taxAmount: toDecimal(summary.taxAmount)
          .plus(toDecimal(cartItem?.taxAmount || 0))
          .toDecimalPlaces(2, Decimal.ROUND_DOWN)
          .toNumber(),
        grandTotal: toDecimal(summary.grandTotal)
          .plus(toDecimal(cartItem?.grandTotal ?? cartItem?.totalPrice ?? 0))
          .toDecimalPlaces(2, Decimal.ROUND_DOWN)
          .toNumber(),
      }),
      {
        taxable: 0,
        taxAmount: 0,
        grandTotal: 0,
      },
    );
  }, [cart]);

  const cartTotal = cartSummary.grandTotal;

  const cartQuantity = useMemo(() => {
    return cart.reduce(
      (total, cartItem) => total + Number(cartItem?.quantity || 0),
      0,
    );
  }, [cart]);

  const handleCategoryClick = (categoryId) => {
    const nextCategoryId =
      selectedCategoryId === categoryId ? null : categoryId;

    setSelectedCategoryId(nextCategoryId);

    filterProducts({
      categoryId: nextCategoryId,
    });
  };

  const handleSearch = (value) => {
    setSearchQuery(value);

    filterProducts({
      search: value,
    });
  };

  const handleProductSelect = (product) => {
    setEditingCartItemId(null);
    setSelectedProduct(product);
    setMissingRequiredAddonId(null);
    setQuantity(1);

    const productAddons = buildVisibleAddons(product);
    const defaults = buildDefaultAddonSelections(productAddons);
    setSelectedAddons(defaults);
    setExhaustedAddonId(
      findExhaustedSelectedAddon(productAddons, defaults)?._id || null,
    );

    setAddonDrawerOpen(true);
  };

  const handleEditCartItem = (cartItem) => {
    const product = allProducts.find(
      (item) => item?._id === cartItem?.productId,
    );

    if (!product) {
      showMessage(
        "This product is no longer available for editing.",
        "warning",
      );
      return;
    }

    const visibleProductAddons = buildVisibleAddons(product);
    const valuesById = visibleProductAddons.reduce((map, addon) => {
      (addon?.values || []).forEach((value) => {
        if (value?._id) {
          map[value._id] = value;
        }
      });
      return map;
    }, {});

    const groupedAddons = (cartItem?.addons || []).reduce(
      (groups, addonValue) => {
        const allocationId = addonValue?.addonAllocationId;

        if (!allocationId) {
          return groups;
        }

        const liveValue = valuesById[addonValue?._id] || addonValue;

        return {
          ...groups,
          [allocationId]: [
            ...(groups[allocationId] || []),
            {
              ...liveValue,
              ...addonValue,
              isExhausted: Boolean(
                liveValue?.isExhausted ?? addonValue?.isExhausted,
              ),
              isDefault: Boolean(liveValue?.isDefault ?? addonValue?.isDefault),
            },
          ],
        };
      },
      {},
    );

    setEditingCartItemId(cartItem?.cartItemId);
    setSelectedProduct(product);
    setSelectedAddons(groupedAddons);
    setMissingRequiredAddonId(null);
    setExhaustedAddonId(
      findExhaustedSelectedAddon(visibleProductAddons, groupedAddons)?._id ||
        null,
    );
    setQuantity(Number(cartItem?.quantity || 1));
    setAddonDrawerOpen(true);
  };

  const closeAddonDrawer = () => {
    setAddonDrawerOpen(false);
    setSelectedProduct(null);
    setSelectedAddons({});
    setMissingRequiredAddonId(null);
    setExhaustedAddonId(null);
    setQuantity(1);
    setEditingCartItemId(null);
  };

  const isAddonValueSelected = (addonAllocationId, addonValueId) => {
    const values = selectedAddons?.[addonAllocationId] || [];

    return values.some((value) => value?._id === addonValueId);
  };

  const handleAddonSelection = (addon, addonValue) => {
    const existingValues = selectedAddons?.[addon?._id] || [];

    const alreadySelected = existingValues.some(
      (value) => value?._id === addonValue?._id,
    );

    // Block choosing an exhausted option, but always allow clearing an
    // already-selected exhausted value so the user can pick another.
    if (addonValue?.isExhausted && !alreadySelected) {
      setExhaustedAddonId(addon?._id);
      showMessage(
        `Please select another value for ${addon?.title || "this addon"}.`,
        "warning",
      );
      return;
    }

    setMissingRequiredAddonId((currentMissingId) =>
      currentMissingId === addon?._id ? null : currentMissingId,
    );

    let nextSelections;

    if (addon?.isMultipleSelect) {
      const updatedValues = alreadySelected
        ? existingValues.filter((value) => value?._id !== addonValue?._id)
        : [...existingValues, enrichAddonValue(addonValue, addon)];

      nextSelections = {
        ...selectedAddons,
        [addon?._id]: updatedValues,
      };
    } else if (alreadySelected && addon?.isOptional) {
      nextSelections = {
        ...selectedAddons,
        [addon?._id]: [],
      };
    } else {
      nextSelections = {
        ...selectedAddons,
        [addon?._id]: [enrichAddonValue(addonValue, addon)],
      };
    }

    setSelectedAddons(nextSelections);
    setExhaustedAddonId(
      findExhaustedSelectedAddon(visibleAddons, nextSelections)?._id || null,
    );
  };

  const scrollToAddonGroup = (addonId) => {
    window.requestAnimationFrame(() => {
      addonGroupRefs.current[addonId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const validateRequiredAddons = () => {
    const missingAddon = visibleAddons.find((addon) => {
      if (addon?.isOptional) {
        return false;
      }

      const selectedValues = selectedAddons?.[addon?._id] || [];

      return selectedValues.length === 0;
    });

    if (!missingAddon) {
      setMissingRequiredAddonId(null);
      return true;
    }

    setMissingRequiredAddonId(missingAddon?._id);
    setExhaustedAddonId(null);
    scrollToAddonGroup(missingAddon?._id);

    showMessage(`Please select ${missingAddon?.title}.`, "warning");

    return false;
  };

  const validateExhaustedAddons = () => {
    const exhaustedAddon = findExhaustedSelectedAddon(
      visibleAddons,
      selectedAddons,
    );

    if (!exhaustedAddon) {
      setExhaustedAddonId(null);
      return true;
    }

    setExhaustedAddonId(exhaustedAddon?._id);
    setMissingRequiredAddonId(null);
    scrollToAddonGroup(exhaustedAddon?._id);

    showMessage(
      `Please select another value for ${exhaustedAddon?.title}.`,
      "warning",
    );

    return false;
  };

  const createCartSignature = (productId, addons = []) => {
    const addonIds = addons
      .map((addonValue) => addonValue?._id)
      .filter(Boolean)
      .sort();

    return `${productId}__${addonIds.join("_")}`;
  };

  const addProductToCart = (product, addons = [], itemQuantity = 1) => {
    if (!product?._id) {
      showMessage("Please select a product.", "warning");
      return;
    }

    try {
      setAddingToCart(true);

      const productPrice = getProductPrice(product);
      const productMrp = getProductMrp(product);
      const addonPrice = addons
        .reduce(
          (total, addon) => total.plus(toDecimal(addon?.price || 0)),
          new Decimal(0),
        )
        .toDecimalPlaces(2, Decimal.ROUND_DOWN)
        .toNumber();

      const unitPrice = toDecimal(productPrice)
        .plus(toDecimal(addonPrice))
        .toDecimalPlaces(2, Decimal.ROUND_DOWN)
        .toNumber();

      const mrpRate = calculateMrpRate(productMrp);

      const productTaxPercent = getProductTaxPercent(product);
      const taxCalculation = calculateTaxAmount(
        unitPrice,
        itemQuantity,
        productTaxPercent,
      );

      const signature = createCartSignature(product._id, addons);

      const existingCartItemIndex = cart.findIndex(
        (cartItem) => cartItem?.signature === signature,
      );

      const updatedCart = [...cart];

      if (existingCartItemIndex >= 0) {
        const existingCartItem = updatedCart[existingCartItemIndex];

        const updatedQuantity =
          Number(existingCartItem?.quantity || 0) + itemQuantity;

        const mergedTaxCalculation = calculateTaxAmount(
          existingCartItem?.unitPrice || unitPrice,
          updatedQuantity,
          existingCartItem?.productTaxPercent ?? productTaxPercent,
        );

        updatedCart[existingCartItemIndex] = {
          ...existingCartItem,
          quantity: updatedQuantity,
          taxable: mergedTaxCalculation.taxable,
          taxAmount: mergedTaxCalculation.taxAmount,
          grandTotal: mergedTaxCalculation.grandTotal,
          totalPrice: mergedTaxCalculation.grandTotal,
        };
      } else {
        updatedCart.push({
          cartItemId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          signature,
          productId: product._id,
          productName: product?.name,
          productImage:
            product?.displayImageUrl || product?.displayImage || Images.product,
          categoryId: product?.category?._id || null,
          categoryName: product?.category?.name || null,
          productPrice,
          productMrp,
          rate: productPrice,
          mrpRate,
          makingTime: Number(product?.makingTime || 0),
          addons,
          hasAddons: buildVisibleAddons(product).length > 0,
          addonPrice,
          unitPrice,
          quantity: itemQuantity,
          productTaxPercent,
          taxable: taxCalculation.taxable,
          taxAmount: taxCalculation.taxAmount,
          grandTotal: taxCalculation.grandTotal,
          totalPrice: taxCalculation.grandTotal,
          orderType: selectedOrderType,
          outletId: outletData?._id || null,
        });
      }

      saveCart(updatedCart);
      showMessage(`${product?.name} added to cart.`);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddToCart = () => {
    if (!selectedProduct?._id) {
      showMessage("Please select a product.", "warning");
      return;
    }

    if (!validateRequiredAddons()) {
      return;
    }

    if (!validateExhaustedAddons()) {
      return;
    }

    const addons = selectedAddonValues.map((addonValue) => ({
      _id: addonValue?._id,
      valueId: getValueMasterId(addonValue?.value) || addonValue?._id,
      addonId: addonValue?.addonId || addonValue?.addonAllocationId,
      addonAllocationId: addonValue?.addonAllocationId,
      addonTitle: addonValue?.addonTitle,
      label: addonValue?.label || getAddonValueLabel(addonValue),
      price: Number(addonValue?.price || 0),
      subLabel: addonValue?.subLabel || null,
      excludedOutlets: addonValue?.excludedOutlets || [],
      addonExcludedOutlets: addonValue?.addonExcludedOutlets || [],
    }));

    if (!editingCartItemId) {
      addProductToCart(selectedProduct, addons, quantity);
      closeAddonDrawer();
      return;
    }

    try {
      setAddingToCart(true);

      const productPrice = getProductPrice(selectedProduct);
      const productMrp = getProductMrp(selectedProduct);
      const addonPrice = addons
        .reduce(
          (total, addon) => total.plus(toDecimal(addon?.price || 0)),
          new Decimal(0),
        )
        .toDecimalPlaces(2, Decimal.ROUND_DOWN)
        .toNumber();

      const unitPrice = toDecimal(productPrice)
        .plus(toDecimal(addonPrice))
        .toDecimalPlaces(2, Decimal.ROUND_DOWN)
        .toNumber();

      const mrpRate = calculateMrpRate(productMrp);

      const productTaxPercent = getProductTaxPercent(selectedProduct);
      const taxCalculation = calculateTaxAmount(
        unitPrice,
        quantity,
        productTaxPercent,
      );

      const signature = createCartSignature(selectedProduct._id, addons);

      const editedItem = cart.find(
        (item) => item?.cartItemId === editingCartItemId,
      );

      if (!editedItem) {
        showMessage("Cart item was not found.", "error");
        closeAddonDrawer();
        return;
      }

      const duplicateItem = cart.find(
        (item) =>
          item?.cartItemId !== editingCartItemId &&
          item?.signature === signature,
      );

      let updatedCart;

      if (duplicateItem) {
        const mergedQuantity =
          Number(duplicateItem?.quantity || 0) + Number(quantity || 1);

        updatedCart = cart
          .filter((item) => item?.cartItemId !== editingCartItemId)
          .map((item) =>
            item?.cartItemId === duplicateItem?.cartItemId
              ? (() => {
                  const mergedTaxCalculation = calculateTaxAmount(
                    item?.unitPrice || unitPrice,
                    mergedQuantity,
                    item?.productTaxPercent ?? productTaxPercent,
                  );

                  return {
                    ...item,
                    quantity: mergedQuantity,
                    taxable: mergedTaxCalculation.taxable,
                    taxAmount: mergedTaxCalculation.taxAmount,
                    grandTotal: mergedTaxCalculation.grandTotal,
                    totalPrice: mergedTaxCalculation.grandTotal,
                  };
                })()
              : item,
          );
      } else {
        updatedCart = cart.map((item) =>
          item?.cartItemId === editingCartItemId
            ? {
                ...item,
                signature,
                productName: selectedProduct?.name,
                productImage:
                  selectedProduct?.displayImageUrl ||
                  selectedProduct?.displayImage ||
                  Images.product,
                categoryId: selectedProduct?.category?._id || null,
                categoryName: selectedProduct?.category?.name || null,
                productPrice,
                productMrp,
                rate: productPrice,
                mrpRate,
                makingTime: Number(selectedProduct?.makingTime || 0),
                addons,
                hasAddons: visibleAddons.length > 0,
                addonPrice,
                unitPrice,
                quantity,
                productTaxPercent,
                taxable: taxCalculation.taxable,
                taxAmount: taxCalculation.taxAmount,
                grandTotal: taxCalculation.grandTotal,
                totalPrice: taxCalculation.grandTotal,
                orderType: selectedOrderType,
              }
            : item,
        );
      }

      saveCart(updatedCart);
      showMessage(`${selectedProduct?.name} updated.`);
      closeAddonDrawer();
    } finally {
      setAddingToCart(false);
    }
  };

  const updateCartQuantity = (cartItemId, quantityChange) => {
    const updatedCart = cart
      .map((cartItem) => {
        if (cartItem?.cartItemId !== cartItemId) {
          return cartItem;
        }

        const updatedQuantity =
          Number(cartItem?.quantity || 1) + quantityChange;

        if (updatedQuantity <= 0) {
          return null;
        }

        const taxCalculation = calculateTaxAmount(
          cartItem?.unitPrice || 0,
          updatedQuantity,
          cartItem?.productTaxPercent || 0,
        );

        return {
          ...cartItem,
          quantity: updatedQuantity,
          taxable: taxCalculation.taxable,
          taxAmount: taxCalculation.taxAmount,
          grandTotal: taxCalculation.grandTotal,
          totalPrice: taxCalculation.grandTotal,
        };
      })
      .filter(Boolean);

    saveCart(updatedCart);
  };

  const removeCartItem = (cartItemId) => {
    const updatedCart = cart.filter(
      (cartItem) => cartItem?.cartItemId !== cartItemId,
    );

    saveCart(updatedCart);
  };

  const clearCart = () => {
    saveCart([]);
    showMessage("Cart cleared.");
  };

  const handleOrderTypeChange = (nextOrderType) => {
    setSelectedOrderType(nextOrderType);
    localStorage.setItem("selectedOrderType", nextOrderType);

    if (!cart.length) {
      return;
    }

    const updatedCart = cart.map((cartItem) => ({
      ...cartItem,
      orderType: nextOrderType,
    }));

    saveCart(updatedCart);
  };

  const buildStockCheckPayload = () => ({
    cartItems: cart.map((cartItem) => ({
      product: cartItem?.productId,
      quantity: Number(cartItem?.quantity || 1),
      addons: (cartItem?.addons || []).map((addon) => {
        const addonPayload = {
          selectedValue:
            addon?.label || addon?.selectedValue || addon?.addonTitle || "",
          price: Number(addon?.price || 0),
          excludedOutlets: addon?.excludedOutlets || [],
          addonExcludedOutlets: addon?.addonExcludedOutlets || [],
        };

        const addonId = addon?.addonId || addon?._id;
        if (addonId != null) {
          addonPayload._id = addonId;
        }

        const isMongoId = (value) =>
          typeof value === "string" && /^[a-f\d]{24}$/i.test(value);

        const selectedValueId = [
          getValueMasterId(addon?.value),
          addon?.valueId,
          addon?.selectedValueId,
          addon?._id,
        ].find(isMongoId);

        if (selectedValueId != null) {
          addonPayload.selectedValueId = selectedValueId;
        }

        return addonPayload;
      }),
    })),
  });

  const handleProcessTransaction = async () => {
    if (cart.length === 0) {
      showMessage("Please add at least one item.", "warning");
      return;
    }

    if (!outletData?._id) {
      showMessage("Outlet information is missing.", "error");
      return;
    }

    try {
      setCheckingStock(true);

      const response = await axios.post(
        `${API.BASE_URL}raw-material/check-raw-material-in-stock-for-outlet`,
        buildStockCheckPayload(),
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        },
      );

      const responseData = response?.data?.data || response?.data;
      const isInStock =
        responseData?.inStock ??
        responseData?.isInStock ??
        response?.data?.status ??
        response?.data?.success ??
        true;

      if (isInStock === false) {
        setCartProductRawMaterials([]);
        showMessage(
          responseData?.error ||
            response?.data?.error ||
            responseData?.message ||
            response?.data?.message ||
            "Some items are out of stock.",
          "error",
        );
        return;
      }

      const checkedRawMaterials = Array.isArray(
        responseData?.cartProductRawMaterials,
      )
        ? responseData.cartProductRawMaterials
        : [];

      setCartProductRawMaterials(checkedRawMaterials);
      setPaymentDrawerOpen(true);
    } catch (error) {
      const errorData = error?.response?.data;
      console.error("Error checking stock:", errorData || error);

      showMessage(
        errorData?.error ||
          errorData?.message ||
          error?.message ||
          "Unable to verify stock. Please try again.",
        "error",
      );
    } finally {
      setCheckingStock(false);
    }
  };

  const closePaymentDrawer = () => {
    setPaymentDrawerOpen(false);
    setCartProductRawMaterials([]);
  };

  const handleOrderCreated = (createdOrder) => {
    saveCart([]);
    setCartProductRawMaterials([]);
    setPaymentDrawerOpen(false);

    showMessage(
      `Order ${createdOrder?.orderNumber || ""} created successfully.`.trim(),
      "success",
    );

    // Optional:
    // navigate(`/orders/${createdOrder?._id}`);
  };

  useEffect(() => {
    getCategoriesProductsList();
  }, [outletData?._id]);

  useEffect(() => {
    if (!allProducts.length || !cart.length) {
      return;
    }

    const hasMissingCartFields = cart.some((cartItem) => {
      if (cartItem?.makingTime == null) {
        return true;
      }

      if (!cartItem?.mrpRate) {
        return true;
      }

      const product = allProducts.find(
        (item) => item?._id === cartItem?.productId,
      );

      if (!product) {
        return false;
      }

      const productMrp = getProductMrp(product);
      return productMrp > 0 && Number(cartItem.mrpRate) !== Number(productMrp);
    });

    if (!hasMissingCartFields) {
      return;
    }

    const updatedCart = cart.map((cartItem) => {
      const product = allProducts.find(
        (item) => item?._id === cartItem?.productId,
      );

      if (!product) {
        return cartItem;
      }

      const updates = {};
      const productMrp = getProductMrp(product);
      const expectedMrpRate = calculateMrpRate(productMrp);

      if (
        !cartItem?.mrpRate ||
        (productMrp > 0 && Number(cartItem.mrpRate) !== Number(expectedMrpRate))
      ) {
        updates.productMrp = productMrp;
        updates.rate = cartItem?.rate ?? cartItem?.productPrice;
        updates.mrpRate = expectedMrpRate;
      }

      if (cartItem?.makingTime == null) {
        updates.makingTime = Number(product?.makingTime || 0);
      }

      return Object.keys(updates).length
        ? { ...cartItem, ...updates }
        : cartItem;
    });

    saveCart(updatedCart);
  }, [allProducts]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F5F6F8",
      }}
    >
      <Grid container>
        {/* Products section */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            height: {
              xs: "auto",
              md: "100vh",
            },
            pt: {
              xs: "70px",
              md: "90px",
            },
            overflow: "hidden",
          }}
        >
          <Box
            className="custom-scrollbar"
            sx={{
              height: {
                xs: "auto",
                md: "100%",
              },
              overflowY: {
                xs: "visible",
                md: "auto",
              },
              px: {
                xs: 2,
                md: 4,
              },
              pb: 4,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                py: 2,
                backgroundColor: "#F5F6F8",
              }}
            >
              <TextField
                fullWidth
                value={searchQuery}
                placeholder="Search product"
                onChange={(event) => handleSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "14px",

                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                  },
                }}
              />

              <Box
                className="custom-scrollbar"
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  pt: 2,
                  pb: 0,
                  justifyContent: "center",
                }}
              >
                {categories.map((category, index) => (
                  <Box key={category._id} sx={{ flex: "0 0 auto" }}>
                    <CategoryFilterChip
                      filterText={category.name}
                      isSelected={selectedCategoryId === category?._id}
                      onClick={() => handleCategoryClick(category?._id)}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>

            {loading ? (
              <Box
                sx={{
                  minHeight: 350,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <CircularProgress
                  sx={{
                    color: "#000",
                    mb: 2,
                  }}
                />

                <Typography>Loading products...</Typography>
              </Box>
            ) : products.length > 0 ? (
              <Grid container spacing={2} sx={{ pt: 0 }}>
                {products.map((product) => {
                  const selected = selectedProduct?._id === product?._id;

                  return (
                    <Grid item xs={6} sm={4} md={4} lg={3} key={product?._id}>
                      <Paper
                        elevation={0}
                        onClick={() => handleProductSelect(product)}
                        sx={{
                          height: "100%",
                          p: 1.5,
                          cursor: "pointer",
                          borderRadius: "18px",
                          border: selected
                            ? "2px solid #00A4E2"
                            : "1px solid #E5E7EB",
                          backgroundColor: "#fff",
                          position: "relative",
                          transition: "0.2s ease",

                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        {selected && (
                          <CheckCircleIcon
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              color: "#00A4E2",
                              backgroundColor: "#fff",
                              borderRadius: "50%",
                            }}
                          />
                        )}

                        <Box
                          sx={{
                            height: 105,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <img
                            src={
                              product?.displayImageUrl ||
                              product?.displayImage ||
                              Images.product
                            }
                            alt={product?.name || "Product"}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100px",
                              objectFit: "contain",
                            }}
                            onError={(event) => {
                              event.currentTarget.onerror = null;

                              event.currentTarget.src = Images.product;
                            }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            minHeight: 37,
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                          }}
                        >
                          {product?.name}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 800,
                            color: "#0076A8",
                            mt: 0.8,
                          }}
                        >
                          ₹{getProductPrice(product).toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  No products found
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Cart section */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            height: {
              xs: "auto",
              md: "100vh",
            },
            pt: {
              xs: 3,
              md: "110px",
            },
            backgroundColor: "#fff",
            borderLeft: {
              xs: "none",
              md: "1px solid #E5E7EB",
            },
          }}
        >
          <Box
            className="custom-scrollbar"
            sx={{
              height: "100%",
              overflowY: "auto",
              px: {
                xs: 2,
                md: 3,
              },
              pb: 4,
            }}
          >
            <CartSection
              cart={cart}
              cartTotal={cartTotal}
              cartTaxable={cartSummary.taxable}
              cartTaxAmount={cartSummary.taxAmount}
              cartQuantity={cartQuantity}
              onIncrease={(cartItemId) => updateCartQuantity(cartItemId, 1)}
              onDecrease={(cartItemId) => updateCartQuantity(cartItemId, -1)}
              onRemove={removeCartItem}
              onEdit={handleEditCartItem}
              onClear={clearCart}
              selectedOrderType={selectedOrderType}
              onOrderTypeChange={handleOrderTypeChange}
              selectedPaymentMethod={selectedPaymentMethod}
              onPaymentMethodChange={setSelectedPaymentMethod}
              onProcessTransaction={handleProcessTransaction}
              checkingStock={checkingStock}
            />
          </Box>
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={addonDrawerOpen}
        onClose={closeAddonDrawer}
        PaperProps={{
          sx: {
            width: {
              xs: "100%",
              sm: 500,
            },
            maxWidth: "100%",
            backgroundColor: "#F7F8FA",
          },
        }}
      >
        {selectedProduct && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                backgroundColor: "#fff",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  variant="rounded"
                  src={
                    selectedProduct?.displayImageUrl ||
                    selectedProduct?.displayImage ||
                    Images.product
                  }
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "16px",
                    backgroundColor: "#F5F6F8",
                    border: "1px solid #ECEFF1",
                  }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {editingCartItemId && (
                    <Typography
                      sx={{
                        mb: 0.4,
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#0076A8",
                        textTransform: "uppercase",
                        letterSpacing: 0.7,
                      }}
                    >
                      Editing cart item
                    </Typography>
                  )}

                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 900,
                      lineHeight: 1.3,
                    }}
                  >
                    {selectedProduct?.name}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 900,
                        color: "#0076A8",
                      }}
                    >
                      ₹{getProductPrice(selectedProduct).toFixed(2)}
                    </Typography>

                    {selectedProduct?.category?.name && (
                      <Chip
                        size="small"
                        label={selectedProduct.category.name}
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Stack>
                </Box>

                <IconButton
                  onClick={closeAddonDrawer}
                  aria-label="Close product drawer"
                  sx={{
                    alignSelf: "flex-start",
                    backgroundColor: "#F5F6F8",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>

            <Box
              className="custom-scrollbar"
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 2,
              }}
            >
              {visibleAddons.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#fff",
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <CheckCircleIcon sx={{ color: "#2E7D32" }} />

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 800,
                        }}
                      >
                        No customisation required
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.3,
                          fontSize: 12.5,
                          color: "#6B7280",
                        }}
                      >
                        Adjust the quantity and add this product to the cart.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ) : (
                <>
                  <Typography
                    sx={{
                      mb: 1.5,
                      fontSize: 13,
                      color: "#6B7280",
                    }}
                  >
                    Select the options you need. Required selections must be
                    completed.
                  </Typography>

                  <Stack spacing={1.5}>
                    {visibleAddons.map((addon, index) => (
                      <Box
                        key={addon?._id}
                        ref={(element) => {
                          addonGroupRefs.current[addon?._id] = element;
                        }}
                      >
                        <AddonGroup
                          addon={addon}
                          index={index}
                          selectedAddons={selectedAddons}
                          isSelected={isAddonValueSelected}
                          onSelect={handleAddonSelection}
                          showValidationError={
                            missingRequiredAddonId === addon?._id
                          }
                          showExhaustedError={exhaustedAddonId === addon?._id}
                        />
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
            </Box>

            <Paper
              square
              elevation={10}
              sx={{
                px: 2.5,
                py: 2,
                backgroundColor: "#fff",
                borderTop: "1px solid #E5E7EB",
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Typography
                      sx={{
                        mb: 0.7,
                        fontSize: 12,
                        color: "#6B7280",
                      }}
                    >
                      Quantity
                    </Typography>

                    <QuantitySelector
                      quantity={quantity}
                      onDecrease={() =>
                        setQuantity((currentQuantity) =>
                          Math.max(1, currentQuantity - 1),
                        )
                      }
                      onIncrease={() =>
                        setQuantity((currentQuantity) => currentQuantity + 1)
                      }
                    />
                  </Box>

                  <Stack
                    spacing={0.4}
                    sx={{
                      minWidth: 180,
                      textAlign: "right",
                    }}
                  >
                    <PriceRow
                      label="Base price"
                      amount={getProductPrice(selectedProduct)}
                    />

                    {addonsTotal > 0 && (
                      <PriceRow label="Customisations" amount={addonsTotal} />
                    )}

                    <PriceRow
                      label={`Tax (${getProductTaxPercent(selectedProduct)}%)`}
                      amount={currentItemTax.taxAmount}
                    />

                    <Divider sx={{ my: 0.4 }} />

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                      >
                        Item total
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 900,
                          color: "#003049",
                        }}
                      >
                        ₹{currentItemTotal.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <Button
                  fullWidth
                  variant="contained"
                  disabled={addingToCart}
                  onClick={handleAddToCart}
                  startIcon={
                    addingToCart ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : editingCartItemId ? (
                      <CheckCircleIcon />
                    ) : (
                      <AddShoppingCartIcon />
                    )
                  }
                  sx={{
                    minHeight: 52,
                    borderRadius: "14px",
                    backgroundColor: "#003049",
                    fontSize: 15,
                    fontWeight: 800,
                    textTransform: "none",
                    boxShadow: "0 8px 18px rgba(0,48,73,0.2)",

                    "&:hover": {
                      backgroundColor: "#002536",
                    },
                  }}
                >
                  {editingCartItemId
                    ? `Update Item · ₹${currentItemTotal.toFixed(2)}`
                    : `Add ${quantity} to Cart · ₹${currentItemTotal.toFixed(
                        2,
                      )}`}
                </Button>
              </Stack>
            </Paper>
          </Box>
        )}
      </Drawer>

      <Drawer
        anchor="right"
        open={paymentDrawerOpen}
        onClose={closePaymentDrawer}
        PaperProps={{
          sx: {
            width: {
              xs: "100%",
              sm: 520,
              md: 580,
            },
            maxWidth: "100%",
            backgroundColor: "#F7F8FA",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <IconButton
            onClick={closePaymentDrawer}
            aria-label="Close payment drawer"
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              zIndex: 10,
              width: 40,
              height: 40,
              color: "#17364A",
              backgroundColor: "rgba(255,255,255,0.92)",
              border: "1px solid #DCE7EC",
              boxShadow: "0 6px 18px rgba(15, 47, 65, 0.14)",
              backdropFilter: "blur(8px)",

              "&:hover": {
                backgroundColor: "#FFFFFF",
                boxShadow: "0 8px 22px rgba(15, 47, 65, 0.18)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box
            className="custom-scrollbar"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <PaymentProcessing
              embedded
              cart={cart}
              outletData={outletData}
              orderType={selectedOrderType}
              paymentMode={selectedPaymentMethod}
              subtotal={cartSummary.taxable}
              taxAmount={cartSummary.taxAmount}
              discountAmount={0}
              grandTotal={cartSummary.grandTotal}
              token={token}
              cartProductRawMaterials={cartProductRawMaterials}
              isCreatingOrder={isCreatingOrder}
              setIsCreatingOrder={setIsCreatingOrder}
              onOrderCreated={handleOrderCreated}
              onOrderError={(errorMessage) => {
                showMessage(errorMessage || "Unable to create order.", "error");
              }}
            />
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={message.open}
        autoHideDuration={2500}
        onClose={() =>
          setMessage((currentMessage) => ({
            ...currentMessage,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={message.severity}
          variant="filled"
          onClose={() =>
            setMessage((currentMessage) => ({
              ...currentMessage,
              open: false,
            }))
          }
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const AddonGroup = ({
  addon,
  index,
  selectedAddons,
  isSelected,
  onSelect,
  showValidationError,
  showExhaustedError,
}) => {
  const selectedValues = selectedAddons?.[addon?._id] || [];

  const selectedCount = selectedValues.length;
  const hasExhaustedSelection = selectedValues.some(
    (value) => value?.isExhausted,
  );
  const showExhaustedMessage = showExhaustedError || hasExhaustedSelection;
  const isComplete =
    (addon?.isOptional || selectedCount > 0) && !hasExhaustedSelection;
  const showError = showValidationError || showExhaustedMessage;

  const handleKeyboardSelection = (event, addonValue) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(addon, addonValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: "18px",
        backgroundColor: "#fff",
        border: showError ? "1.5px solid #D32F2F" : "1px solid #E5E7EB",
        boxShadow: showError ? "0 0 0 3px rgba(211,47,47,0.08)" : "none",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ mb: 1.4 }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            flex: "0 0 auto",
            borderRadius: "50%",
            backgroundColor: isComplete ? "#EAF8EE" : "#FFF1F1",
            color: isComplete ? "#2E7D32" : "#C62828",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          {isComplete ? "✓" : index + 1}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 900,
              }}
            >
              {addon?.title}
            </Typography>

            <Chip
              size="small"
              label={addon?.isOptional ? "Optional" : "Required"}
              sx={{
                height: 21,
                fontSize: 10.5,
                fontWeight: 800,
                backgroundColor: addon?.isOptional ? "#F3F4F6" : "#FFF0F0",
                color: addon?.isOptional ? "#4B5563" : "#C62828",
              }}
            />
          </Stack>

          <Typography
            sx={{
              mt: 0.35,
              fontSize: 12,
              color: "#6B7280",
            }}
          >
            {addon?.isMultipleSelect
              ? "Select one or more options"
              : "Select one option"}
          </Typography>
        </Box>

        {selectedCount > 0 && !hasExhaustedSelection && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: "#2E7D32",
            }}
          >
            {selectedCount} selected
          </Typography>
        )}
      </Stack>

      <Stack spacing={0.8}>
        {addon?.values?.map((addonValue) => {
          const selected = isSelected(addon?._id, addonValue?._id);
          const isExhausted = Boolean(addonValue?.isExhausted);

          return (
            <Box
              key={addonValue?._id}
              role={addon?.isMultipleSelect ? "checkbox" : "radio"}
              aria-checked={selected}
              aria-disabled={isExhausted && !selected}
              tabIndex={0}
              onClick={() => onSelect(addon, addonValue)}
              onKeyDown={(event) => handleKeyboardSelection(event, addonValue)}
              sx={{
                minHeight: 52,
                px: 1.3,
                py: 1,
                cursor: isExhausted
                  ? selected
                    ? "pointer"
                    : "not-allowed"
                  : "pointer",
                borderRadius: "13px",
                border: selected
                  ? isExhausted
                    ? "2px solid #D32F2F"
                    : "2px solid #00A4E2"
                  : isExhausted
                    ? "1px solid #F5B5B5"
                    : "1px solid #E5E7EB",
                backgroundColor: selected
                  ? isExhausted
                    ? "#FFF1F1"
                    : "#EFFAFF"
                  : isExhausted
                    ? "#FFF8F8"
                    : "#fff",
                opacity: isExhausted && !selected ? 0.72 : 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                transition: "0.15s ease",

                "&:hover": {
                  borderColor: isExhausted ? "#D32F2F" : "#00A4E2",
                  backgroundColor: isExhausted ? "#FFF1F1" : "#F7FCFE",
                },

                "&:focus-visible": {
                  outline: "3px solid rgba(0,164,226,0.2)",
                  outlineOffset: 2,
                },
              }}
            >
              {addon?.isMultipleSelect ? (
                <Box
                  sx={{
                    width: 21,
                    height: 21,
                    borderRadius: "6px",
                    flex: "0 0 auto",
                    border: selected
                      ? isExhausted
                        ? "2px solid #D32F2F"
                        : "2px solid #00A4E2"
                      : "2px solid #B8BDC5",
                    backgroundColor: selected
                      ? isExhausted
                        ? "#D32F2F"
                        : "#00A4E2"
                      : "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                  }}
                >
                  {selected && <CheckCircleIcon sx={{ fontSize: 16 }} />}
                </Box>
              ) : (
                <Radio
                  checked={selected}
                  tabIndex={-1}
                  sx={{
                    p: 0,
                    pointerEvents: "none",

                    "&.Mui-checked": {
                      color: isExhausted ? "#D32F2F" : "#00A4E2",
                    },
                  }}
                />
              )}

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.7}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 750,
                      textDecoration: isExhausted ? "line-through" : "none",
                      color: isExhausted ? "#9CA3AF" : "inherit",
                    }}
                  >
                    {addonValue?.label}
                  </Typography>

                  {addonValue?.isDefault && (
                    <Chip
                      size="small"
                      label="Default"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 800,
                        backgroundColor: "#FFF4DF",
                        color: "#B26A00",
                      }}
                    />
                  )}

                  {isExhausted && (
                    <Chip
                      size="small"
                      label="Out of stock"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 800,
                        backgroundColor: "#FEE2E2",
                        color: "#B91C1C",
                      }}
                    />
                  )}
                </Stack>

                {addonValue?.subLabel &&
                  addonValue?.subLabel !== addonValue?.label && (
                    <Typography
                      sx={{
                        mt: 0.2,
                        fontSize: 11.5,
                        color: "#6B7280",
                      }}
                    >
                      {addonValue.subLabel}
                    </Typography>
                  )}
              </Box>

              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color:
                    Number(addonValue?.price || 0) > 0 ? "#111827" : "#6B7280",
                }}
              >
                {Number(addonValue?.price || 0) > 0
                  ? `+₹${Number(addonValue.price).toFixed(2)}`
                  : "Included"}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {showValidationError && (
        <Typography
          sx={{
            mt: 1,
            fontSize: 11.5,
            color: "#C62828",
            fontWeight: 700,
          }}
        >
          Please select an option.
        </Typography>
      )}

      {showExhaustedMessage && (
        <Typography
          sx={{
            mt: 1,
            fontSize: 11.5,
            color: "#C62828",
            fontWeight: 700,
          }}
        >
          Please select another value for {addon?.title || "this addon"}.
        </Typography>
      )}
    </Paper>
  );
};

const PriceRow = ({ label, amount }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography
      sx={{
        fontSize: 12,
        color: "#6B7280",
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      ₹{Number(amount || 0).toFixed(2)}
    </Typography>
  </Stack>
);

const QuantitySelector = ({ quantity, onDecrease, onIncrease }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        p: 0.4,
      }}
    >
      <IconButton size="small" onClick={onDecrease} disabled={quantity <= 1}>
        <RemoveIcon fontSize="small" />
      </IconButton>

      <Typography
        sx={{
          minWidth: 24,
          textAlign: "center",
          fontWeight: 800,
        }}
      >
        {quantity}
      </Typography>

      <IconButton size="small" onClick={onIncrease}>
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

const CartSection = ({
  cart,
  cartTotal,
  cartTaxable,
  cartTaxAmount,
  cartQuantity,
  onIncrease,
  onDecrease,
  onRemove,
  onEdit,
  onClear,
  selectedOrderType,
  onOrderTypeChange,
  selectedPaymentMethod,
  onPaymentMethodChange,
  onProcessTransaction,
  checkingStock = false,
}) => {
  const cartScrollRef = useRef(null);
  const [remainingCartItems, setRemainingCartItems] = useState(0);

  const discount = 0;
  const payableTotal = Math.max(cartTotal - discount, 0);

  const updateRemainingCartItems = () => {
    const scrollContainer = cartScrollRef.current;

    if (!scrollContainer || cart.length === 0) {
      setRemainingCartItems(0);
      return;
    }

    const containerBottom = scrollContainer.getBoundingClientRect().bottom;

    const cartItemElements = Array.from(
      scrollContainer.querySelectorAll('[data-cart-item="true"]'),
    );

    const remainingCount = cartItemElements.filter(
      (itemElement) =>
        itemElement.getBoundingClientRect().bottom > containerBottom + 1,
    ).length;

    setRemainingCartItems(remainingCount);
  };

  const scrollToNextCartItem = () => {
    const scrollContainer = cartScrollRef.current;

    if (!scrollContainer) {
      return;
    }

    const containerBottom = scrollContainer.getBoundingClientRect().bottom;

    const nextHiddenItem = Array.from(
      scrollContainer.querySelectorAll('[data-cart-item="true"]'),
    ).find(
      (itemElement) =>
        itemElement.getBoundingClientRect().bottom > containerBottom + 1,
    );

    if (nextHiddenItem) {
      nextHiddenItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  useEffect(() => {
    const frameId = window.requestAnimationFrame(updateRemainingCartItems);

    window.addEventListener("resize", updateRemainingCartItems);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateRemainingCartItems);
    };
  }, [cart]);

  const selectionCardSx = (selected) => ({
    flex: 1,
    minWidth: 0,
    minHeight: 86,
    px: 1,
    py: 1.2,
    borderRadius: "12px",
    cursor: "pointer",
    border: selected ? "2px solid #BFA77A" : "1px solid #D9D9D9",
    backgroundColor: selected ? "#FFF9EE" : "#fff",
    boxShadow: selected
      ? "0 4px 10px rgba(112, 84, 34, 0.18)"
      : "0 3px 8px rgba(0,0,0,0.10)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
    userSelect: "none",

    "&:hover": {
      transform: "translateY(-1px)",
    },
  });

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.4 }}
      >
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: 0.3,
          }}
        >
          Cart
        </Typography>

        {cart.length > 0 && (
          <Button
            size="small"
            onClick={onClear}
            sx={{
              minWidth: "auto",
              color: "#C62828",
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Clear
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 170,
          minWidth: 0,
        }}
      >
        <Box
          ref={cartScrollRef}
          className="custom-scrollbar"
          onScroll={updateRemainingCartItems}
          sx={{
            height: "100%",
            overflowY: "auto",
            pr: 0.4,
            pb: remainingCartItems > 0 ? 7 : 0,
          }}
        >
          {cart.length > 0 ? (
            <Stack spacing={1}>
              {cart.map((cartItem) => {
                const addonCount = cartItem?.addons?.length || 0;

                return (
                  <Paper
                    key={cartItem?.cartItemId}
                    data-cart-item="true"
                    elevation={0}
                    sx={{
                      p: 1.25,
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      border: "1px solid #DADADA",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.07)",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                      >
                        <Box
                          onClick={() => onEdit(cartItem)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onEdit(cartItem);
                            }
                          }}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            cursor: "pointer",
                            outline: "none",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.8}
                            alignItems="center"
                          >
                            <Typography
                              title={cartItem?.productName}
                              sx={{
                                display: "-webkit-box",
                                flex: 1,
                                minWidth: 0,
                                fontSize: 14.5,
                                fontWeight: 900,
                                lineHeight: 1.3,
                                color: "#0F5F7F",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                overflow: "hidden",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {cartItem?.productName}
                            </Typography>

                            <Box
                              sx={{
                                minWidth: 28,
                                height: 24,
                                px: 0.6,
                                borderRadius: "7px",
                                backgroundColor: "#E8F4FA",
                                color: "#0F5F7F",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 11.5,
                                  fontWeight: 900,
                                  lineHeight: 1,
                                }}
                              >
                                {Number(cartItem?.quantity || 1)} nos
                              </Typography>
                            </Box>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={0.6}
                            alignItems="center"
                            sx={{
                              mt: 0.45,
                              minWidth: 0,
                            }}
                          >
                            {addonCount > 0 ? (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  maxWidth: "100%",
                                  px: 0.8,
                                  py: 0.35,
                                  borderRadius: "7px",
                                  backgroundColor: "#F0F7FA",
                                  color: "#0F5F7F",
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: 11,
                                    lineHeight: 1,
                                  }}
                                >
                                  🧩
                                </Typography>

                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: 10.8,
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {addonCount}{" "}
                                  {addonCount === 1
                                    ? "Customization"
                                    : "Customizations"}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: 10.8,
                                  color: "#8A8A8A",
                                  fontWeight: 600,
                                }}
                              >
                                Tap to edit
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        <IconButton
                          size="small"
                          aria-label={`Remove ${
                            cartItem?.productName || "item"
                          }`}
                          onClick={() => onRemove(cartItem?.cartItemId)}
                          sx={{
                            width: 27,
                            height: 27,
                            p: 0,
                            color: "#C62828",
                            backgroundColor: "#FFF1F1",

                            "&:hover": {
                              backgroundColor: "#FFE3E3",
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Box
              sx={{
                height: 150,
                border: "1px dashed #CACACA",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 2,
              }}
            >
              <Typography sx={{ fontSize: 14, color: "#777" }}>
                Your cart is empty.
              </Typography>
            </Box>
          )}
        </Box>

        {remainingCartItems > 0 && (
          <Box
            onClick={scrollToNextCartItem}
            role="button"
            tabIndex={0}
            aria-label={`Scroll to view ${remainingCartItems} more ${
              remainingCartItems === 1 ? "product" : "products"
            }`}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                scrollToNextCartItem();
              }
            }}
            sx={{
              position: "absolute",
              right: "2%",
              bottom: -1,
              zIndex: 3,
              width: 42,
              minHeight: 26,
              borderRadius: "42px 42px 0 0",
              border: "5px solid #FFE400",
              borderBottom: 0,
              backgroundColor: "#10A8E8",
              color: "#FFE400",
              boxShadow: "6px 0 10px rgba(0,0,0,0.25)",
              cursor: "pointer",
              userSelect: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 0.8,
              pt: 0.3,
              pb: 0.3,
              transition: "0.15s ease",

              "&:hover": {
                backgroundColor: "#0799D7",
                transform: "translateX(-50%) translateY(-2px)",
              },

              "&:focus-visible": {
                outline: "3px solid #003049",
                outlineOffset: "3px",
              },
            }}
          >
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 14,
                lineHeight: 1,
                mb: -0.6,
                transform: "rotate(180deg)",
              }}
            />

            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 900,
                lineHeight: 0.95,
                letterSpacing: -1,
              }}
            >
              {remainingCartItems}
            </Typography>

            <Typography
              sx={{
                mt: 0.35,
                fontSize: 7,
                fontWeight: 900,
                lineHeight: 1,
                textTransform: "uppercase",
              }}
            >
              More
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ pt: 1.5 }}>
        <Box
          sx={{
            borderTop: "1px dashed #BDBDBD",
            borderBottom: "1px dashed #BDBDBD",
            py: 1.2,
          }}
        >
          <Stack spacing={0.8}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, color: "#777" }}>
                Items
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#777" }}>
                {cartQuantity} {cartQuantity === 1 ? "item" : "items"}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, color: "#777" }}>
                Subtotal
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#777" }}>
                ₹{Number(cartTaxable || 0).toFixed(2)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, color: "#777" }}>Tax</Typography>
              <Typography sx={{ fontSize: 11, color: "#777" }}>
                ₹{Number(cartTaxAmount || 0).toFixed(2)}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ py: 1.1 }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 900 }}>TOTAL</Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 900 }}>
            ₹{payableTotal.toFixed(2)}
          </Typography>
        </Stack>

        <SegmentedControls
          value={selectedOrderType}
          onChange={onOrderTypeChange}
          options={[
            {
              value: "DINE_IN",
              label: "Dine In",
              icon: RestaurantIcon,
            },
            {
              value: "TAKEAWAY",
              label: "Takeaway",
              icon: TakeoutDiningIcon,
            },
          ]}
        />

        <SegmentedControls
          value={selectedPaymentMethod}
          onChange={onPaymentMethodChange}
          options={[
            {
              value: "UPI",
              label: "UPI",
              icon: QrCode2Icon,
            },
            {
              value: "CARD",
              label: "Card",
              icon: CreditCardOutlinedIcon,
            },
          ]}
        />

        <Button
          fullWidth
          variant="contained"
          disabled={cart.length === 0 || checkingStock}
          onClick={onProcessTransaction}
          sx={{
            mt: 1,
            minHeight: 50,
            borderRadius: "9px",
            color: "#fff",
            fontSize: 15,
            fontWeight: 900,
            textTransform: "none",
            boxShadow: "none",
            background: "linear-gradient(135deg, #003049 0%, #0f5f7f 100%)",
            boxShadow: "0 10px 22px rgba(0,48,73,0.22)",
            "&:hover": {
              background: "linear-gradient(135deg, #002433 0%, #0b4d68 100%)",
            },
            "&.Mui-disabled": {
              background: "#c7cdd2",
              color: "#fff",
              boxShadow: "none",
            },
          }}
        >
          {checkingStock ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            "Payment"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CafeOrdering;
